import { z } from 'zod';
import { stringToList } from '#screens/hosts/form/advanced/mappers/stringToList.ts';
import {
  ipv4OptionalPortRegex,
  ipv4RequiredPortRegex,
  ipv6OptionalPortRegex,
  ipv6RequiredPortRegex,
} from './helpers/ip-address.ts';
import { invalidPortAllowZero } from './helpers/port.ts';

export const INVALID_HOST_OR_PORT = 'Invalid host or port';
export const ERR_MISSING_PORT = 'Must include a port (can be :0 to use actual listening port)';
export const INVALID_PORT_ERR = 'Invalid port';

/**
 * Check regex errors to see if we can be a bit more exact, and throw a port error message instead
 */
const errorMap: z.ZodErrorMap = (issue, ctx) => {
  if (issue.code === 'invalid_string' && issue.validation === 'regex') {
    if (
      invalidPortAllowZero(ctx.data, ipv4RequiredPortRegex) ||
      invalidPortAllowZero(ctx.data, ipv6RequiredPortRegex)
    ) {
      return { message: INVALID_PORT_ERR };
    }

    if (ipv4OptionalPortRegex.test(ctx.data) || ipv6OptionalPortRegex.test(ctx.data)) {
      return { message: ERR_MISSING_PORT };
    }

    return { message: INVALID_HOST_OR_PORT };
  }
  return { message: ctx.defaultError };
};

const ipv6AllowZeroPortSchema = z
  .string({ errorMap })
  .regex(ipv6RequiredPortRegex)
  .refine((value) => !invalidPortAllowZero(value, ipv6RequiredPortRegex), INVALID_PORT_ERR);

const ipv4AllowZeroPortSchema = z
  .string({ errorMap })
  .regex(ipv4RequiredPortRegex)
  .refine((value) => !invalidPortAllowZero(value, ipv4RequiredPortRegex), INVALID_PORT_ERR);

/**
 * {@link IPAndRequiredPortListSchema} takes in a comma-separated string and validates that it contains only valid ipv4/ipv6 addresses
 * with a required port (which can be zero). It will also accept an empty string.
 */
export const IPAndRequiredPortListSchema = z
  .string()
  .transform((value) => (typeof value === 'string' ? stringToList(value) : undefined))
  .pipe(z.union([ipv6AllowZeroPortSchema, ipv4AllowZeroPortSchema]).array())
  .or(z.literal(''));
